/**
 * Save logic for device edit (name, avatar, room).
 * Extracted from EditDeviceWizard.onFinish for use in DeviceDetails screen.
 * EditDeviceWizard is NOT modified - it keeps its own onFinish logic.
 */

import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { toast } from '@Shelly/shelly-design-system';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { requestEditDevice, requestEditSharedDevice } from 'src/components/Lists/list-helpers';
import {
	get_gen_2_component_channel_structure,
	get_gen_2_component_from_category,
	get_local_ip,
	is_gen_2,
	real_id,
} from 'src/helpers/interface_helpers';
import { localReq, postReq } from 'src/services/http';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { DEVICE_NAME_SYS_SYNC_GEN_2_DEVICES } from 'src/constants';
import { getComponentByDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { syncPeripheralDeviceName } from 'src/helpers/addons/addon_settings_helpers';
import type { Device } from 'src/types/Device';

export async function performDeviceEditSave(editDevice: Device): Promise<void> {
	const $format = get(format);
	const $devices = get(devices);
	const $devices_settings = get(devices_settings);
	const $devices_status = get(devices_status);

	editDevice.name = editDevice.name.trim();
	if (editDevice.name === '') return;

	if (editDevice.gen !== 'GBLE') {
		const settings = $devices_settings[real_id(editDevice.id)];
		const status = $devices_status[real_id(editDevice.id)];
		const isOffline = !status?.online || typeof settings === 'undefined';

		editDevice.name_sync = $devices[editDevice.id]?.['name_sync'];

		if (/_(2|12|22)\d{2}$/.test(editDevice.id) && !editDevice.shared) {
			const trv = Object.keys(settings || {}).filter((i) => /^(blutrv):2\d{2}$/.test(i));
			for (const component in status) {
				if (!settings?.[component]) {
					continue;
				} else if (/^(group|bthomedevice):2\d{2}$/.test(component)) {
					if (
						(editDevice.channel >= 200 && component != getComponentByDevice(editDevice)) ||
						trv.find((i) => settings[i]?.trv == component)
					) {
						continue;
					}
					devices_settings.update_devices_settings(real_id(editDevice.id), {
						...$devices_settings[real_id(editDevice.id)],
						[component]: {
							...$devices_settings[real_id(editDevice.id)][component],
							name: editDevice.name,
						},
					});
				}
			}
			delete editDevice.name_sync;
			toast.open({
				type: 'success',
				title: $format('virtual_device_name_scc_toast', {
					default: 'Virtual device name changed to {name}.',
					values: { name: editDevice.name },
				}),
			});
		} else if (!editDevice.shared && isPeripheralDevice(editDevice.type)) {
			syncPeripheralDeviceName(editDevice);
		}

		try {
			if (editDevice.name_sync) {
				let cacheName = settings?.name;
				let isGen2 = is_gen_2(editDevice);

				if (cacheName !== editDevice.name) {
					if (isGen2) {
						if (DEVICE_NAME_SYS_SYNC_GEN_2_DEVICES.includes(editDevice.type)) {
							const params = {
								id: editDevice.channel,
								config: { device: { name: editDevice.name } },
							};
							const res: SettingRequest = isOffline
								? { isok: true }
								: await gen2Request(editDevice, 'Sys.SetConfig', params);

							if (res.isok) {
								devices.update(editDevice);
								const int_res = await postReq('interface/device/save', {
									id: editDevice.id,
									data: JSON.stringify(editDevice),
								});
								if (!int_res.isok) {
									toast.open({
										type: 'danger',
										title: $format('error_global'),
									});
								}

								toast.open({
									type: 'success',
									title:
										$format('device_name_scc_toast', {
											values: { value: editDevice.name },
										}) || `Device name changed to ${editDevice.name}`,
								});
								settings.sys.device.name = editDevice.name;
								devices_settings.update_devices_settings(editDevice.id, settings);
							} else {
								toast.open({
									type: 'danger',
									title: $format('device_name_err_toast', {
										default: 'Cloud not change device name.',
									}),
								});
							}
						} else {
							const params = {
								id: editDevice.channel,
								config: { name: editDevice.name },
							};
							const method = `${get_gen_2_component_from_category(editDevice)}.SetConfig`;
							const res: SettingRequest = isOffline
								? { isok: true }
								: await gen2Request(editDevice, method, params);

							if (res.isok) {
								toast.open({
									type: 'success',
									title:
										$format('device_name_scc_toast', {
											values: { value: editDevice.name },
										}) || `Device name changed to ${editDevice.name}`,
								});
							} else {
								toast.open({
									type: 'danger',
									title: $format('device_name_err_toast', {
										default: 'Cloud not change device name.',
									}),
								});
							}
						}
					} else {
						const id = real_id(editDevice.id);
						const localIp = get_local_ip(id);
						let res;

						if (editDevice.channels_count > 1) {
							if (localIp) {
								res = await localReq(editDevice, `${localIp}/settings`, 'post', {
									name: editDevice.name,
									channel:
										editDevice.type !== 'SHEM-3' ? editDevice.channel : editDevice,
								});
							} else {
								res = await postReq(`/device/rename`, {
									id: id,
									name: editDevice.name.length > 0 ? editDevice.name : 'null',
									channel:
										editDevice.type !== 'SHEM-3'
											? editDevice.channel
											: editDevice.channel,
								});
							}
						} else {
							if (localIp) {
								res = await localReq(editDevice, `${localIp}/settings`, 'post', {
									name: editDevice.name,
								});
							} else {
								res = await postReq(`/device/rename`, {
									id: id,
									name: editDevice.name.length > 0 ? editDevice.name : 'null',
								});
							}
						}

						if (res?.isok) {
							toast.open({
								type: 'success',
								title:
									$format('device_name_scc_toast', {
										values: { value: editDevice.name },
									}) || `Device name changed to ${editDevice.name}`,
							});
						} else {
							toast.open({
								type: 'danger',
								title: $format('device_name_err_toast', {
									default: 'Cloud not change device name.',
								}),
							});
						}
					}
				}

				if (is_gen_2(editDevice) && settings) {
					settings[`${get_gen_2_component_channel_structure(editDevice)}`]['name'] =
						editDevice.name;

					devices_settings.update_devices_settings(editDevice.id, settings);
				} else if (settings) {
					const category =
						editDevice.category !== 'inputs_reader' ? editDevice.category + 's' : 'inputs';
					const channel = 0;

					settings[category][
						editDevice.type !== 'SHEM-3' ? editDevice.channel : channel
					].name = editDevice.name;

					if (editDevice.channels_count < 2) {
						settings.name = editDevice.name;
						devices.update(editDevice);
						const int_res = await postReq('interface/device/save', {
							id: editDevice.id,
							data: JSON.stringify(editDevice),
						});
						if (int_res.isok) {
							devices_settings.update_devices_settings(editDevice.id, settings);
							toast.open({
								type: 'success',
								title:
									$format('chan_name_succ', {
										values: { name: editDevice.name },
									}) || `Channel name was changed to ${editDevice.name}`,
							});
						} else {
							toast.open({
								type: 'danger',
								title: $format('error_global'),
							});
						}
					}
				}
			}
		} catch (error) {
			console.error('Failed to "Syn Name"! Error:', error);
		}
	}

	if (editDevice.shared) {
		await requestEditSharedDevice(editDevice);
	} else {
		await requestEditDevice(editDevice);
	}
}
